/**
 * The Janitor — a non-combatant NPC (torso 'J') that lives in a supply closet
 * and mops up blood when a guard calls it in, once the threat's been dealt with.
 * It walks to each bloody tile, mops it clean, and returns to the closet when
 * the floor is spotless.
 *
 * Structurally a `Guard` (so it reuses movement + pathfinding + the two-tile
 * render model), but kept in its OWN array so it's excluded from every guard
 * behaviour pass (awareness, patrol, dispatch, combat, flashlights). Headless.
 */

import { CONFIG } from '../config';
import { isBlinded, isBlindStunned } from './blind';
import type { BloodMap } from './blood';
import { hasCoverBetween } from './cover';
import { updateDoorMemory } from './doorWatch';
import { computeFov } from './fov';
import type { Item } from './inventory';
import { moveTowards, guardSpeed, spawnGuard, tryCloseKnownDoor, type Guard } from './guard';
import { lightLevelAt, visibilityFactor } from './light';
import { inBounds, isBlocked, tileAt, type MapData } from './map';
import { syncVisual } from './movement';
import type { Player } from './player';
import { blocksVision } from './tiles';
import type { World } from './world';

export interface Janitor extends Guard {
  /** A guard has summoned the janitor to clean. */
  janitorCalled: boolean;
  /** The bloody tile it's currently heading to mop, or null. */
  cleanTargetX: number | null;
  cleanTargetY: number | null;
  /** Home closet tile it idles at / returns to. */
  closetX: number;
  closetY: number;
  /** Seconds spent mopping the current tile. */
  mopTimer: number;
  /** Ticks left to reach the current target before abandoning it. */
  reachTimeout: number;
  /** Running to warn a guard about a spotted intruder (no walkie-talkie). */
  alerting: boolean;
  /** Id of the guard it's running to warn, or null. */
  alertGuardId: number | null;
  /** Where it last saw the intruder — the report it delivers. */
  alertPlayerX: number;
  alertPlayerY: number;
  /** The closet key it carries — drops as loot on death (a quiet way into the
   *  cabinet). null once looted or if it holds no key. */
  heldKey: Item | null;
}

const JANITOR_FG = 0x66d9c0; // teal — clearly not a guard

/** Spawn the janitor idling in its closet. */
export function spawnJanitor(world: World, x: number, y: number): Janitor {
  const g = spawnGuard(world, x, y) as Janitor;
  g.glyph = 'J';
  g.fg = JANITOR_FG;
  g.isJanitor = true;
  g.lastSeenGlyph = 'J';
  g.lastSeenFg = JANITOR_FG;
  g.janitorCalled = false;
  g.cleanTargetX = null;
  g.cleanTargetY = null;
  g.closetX = x;
  g.closetY = y;
  g.mopTimer = 0;
  g.reachTimeout = 0;
  g.alerting = false;
  g.alertGuardId = null;
  g.alertPlayerX = 0;
  g.alertPlayerY = 0;
  g.heldKey = null; // testMap assigns the closet key after the lock is created
  return g;
}

/** Summon an idle janitor to clean. Returns true if one accepted the job. */
export function callJanitor(janitors: readonly Janitor[]): boolean {
  for (const j of janitors) {
    if (!j.body.dead && !j.janitorCalled) {
      j.janitorCalled = true;
      return true;
    }
  }
  return false;
}

/** What the janitor did this tick, for the caller to log. */
export type JanitorEvent = 'done' | 'sawIntruder' | 'informed' | null;

/**
 * Advance the janitor one tick. Priority: warn a guard about a spotted intruder
 * (running to them, no radio) → mop blood when called → idle in the closet.
 * `guards` lets it spot the intruder + pick who to warn; `player` is the intruder.
 * `occupied` should report guards + player + other janitors.
 */
export function updateJanitor(
  janitor: Janitor,
  map: MapData,
  blood: BloodMap,
  guards: readonly Guard[],
  player: Player,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
): JanitorEvent {
  if (janitor.body.dead) { janitor.fovTiles = null; syncVisual(janitor); return null; }

  // Vision cone. Like every alive entity, the janitor projects a rendered cone —
  // but its sight is OMNIDIRECTIONAL (see janitorSeesIntruder), so its cone is a
  // full 360° disc out to `sightRange`, occluded by walls. Computed each tick
  // from the current tile (stable across an in-progress step) so the renderer's
  // vision-field picks it up exactly as it does a guard's.
  computeJanitorFov(janitor, map);
  // Note doors it can see (builds "known closed" memory + picks one to shut near
  // its closet). The walk-and-shut fires in the idle branch below.
  updateDoorMemory(janitor, map);

  // Finish an in-progress step (same step-locked model as guards).
  if (janitor.moveProgress > 0) {
    const diag = janitor.moveDx !== 0 && janitor.moveDy !== 0;
    const rate = guardSpeed(janitor) * (diag ? 1 / Math.SQRT2 : 1);
    janitor.moveProgress += rate * tickDt;
    if (janitor.moveProgress >= 1) {
      janitor.x += janitor.moveDx;
      janitor.y += janitor.moveDy;
      janitor.moveProgress = 0;
      janitor.moveDx = 0;
      janitor.moveDy = 0;
      if (janitor.path && janitor.path.idx < janitor.path.steps.length - 1) janitor.path.idx++;
    }
    syncVisual(janitor);
    return null;
  }

  // Flash-blinded + still stunned → dazed, can't move (blind already blocks
  // its spotting above).
  if (isBlindStunned(janitor)) { syncVisual(janitor); return null; }

  // -- Spotting an intruder: run to the nearest guard to warn them --
  let event: JanitorEvent = null;
  if (janitorSeesIntruder(janitor, player, map)) {
    janitor.alertPlayerX = player.x;
    janitor.alertPlayerY = player.y;
    if (!janitor.alerting) {
      const guard = nearestLiveGuard(janitor, guards);
      if (guard) {
        janitor.alerting = true;
        janitor.alertGuardId = guard.id;
        janitor.path = null;
        event = 'sawIntruder';
      }
    }
  }
  if (janitor.alerting) {
    const guard = janitor.alertGuardId !== null
      ? guards.find((g) => g.id === janitor.alertGuardId && !g.body.dead)
      : undefined;
    if (!guard) {
      janitor.alerting = false; // its guard is gone — fall through to normal behaviour
      janitor.alertGuardId = null;
    } else if (Math.max(Math.abs(janitor.x - guard.x), Math.abs(janitor.y - guard.y)) <= 1) {
      informGuard(guard, janitor.alertPlayerX, janitor.alertPlayerY);
      janitor.alerting = false;
      janitor.alertGuardId = null;
      syncVisual(janitor);
      return 'informed';
    } else {
      moveTowards(janitor, guard.x, guard.y, map, occupied, tickDt, { adjacent: true });
      syncVisual(janitor);
      return event;
    }
  }
  if (event) { syncVisual(janitor); return event; }

  // Not called → tidy up (shut a door near the closet it knew closed but now sees
  // open) and otherwise head home to the closet and idle.
  if (!janitor.janitorCalled) {
    if (tryCloseKnownDoor(janitor, map, occupied, tickDt)) {
      syncVisual(janitor);
      return null;
    }
    if (janitor.x !== janitor.closetX || janitor.y !== janitor.closetY) {
      moveTowards(janitor, janitor.closetX, janitor.closetY, map, occupied, tickDt, {});
    }
    syncVisual(janitor);
    return null;
  }

  // Called → acquire a bloody tile if we don't have one.
  if (janitor.cleanTargetX === null || janitor.cleanTargetY === null) {
    const spot = nearestBlood(blood, map, janitor.x, janitor.y);
    if (!spot) {
      janitor.janitorCalled = false; // floor's clean → go home
      syncVisual(janitor);
      return 'done';
    }
    janitor.cleanTargetX = spot.x;
    janitor.cleanTargetY = spot.y;
    janitor.mopTimer = 0;
    janitor.reachTimeout = CONFIG.janitor.reachTimeoutTicks;
    janitor.path = null;
  }

  const tx = janitor.cleanTargetX;
  const ty = janitor.cleanTargetY;

  // At the tile → mop it.
  if (janitor.x === tx && janitor.y === ty) {
    janitor.mopTimer += tickDt;
    if (janitor.mopTimer >= CONFIG.janitor.mopSecPerTile || blood.at(tx, ty) <= 0) {
      blood.clearTile(tx, ty);
      janitor.cleanTargetX = null;
      janitor.cleanTargetY = null;
      janitor.mopTimer = 0;
    }
    syncVisual(janitor);
    return null;
  }

  // Walk to it; give up (and wipe it, so we don't loop) if it stays unreachable.
  janitor.reachTimeout -= 1;
  if (janitor.reachTimeout <= 0) {
    blood.clearTile(tx, ty);
    janitor.cleanTargetX = null;
    janitor.cleanTargetY = null;
    syncVisual(janitor);
    return null;
  }
  moveTowards(janitor, tx, ty, map, occupied, tickDt, {});
  syncVisual(janitor);
  return null;
}

/** Omnidirectional 360° FOV disc out to `sightRange`, occluded by walls — the
 *  janitor's rendered vision cone. Mirrors what `janitorSeesIntruder` can spot
 *  (same range + LOS occlusion, no facing filter). Written to `fovTiles` so the
 *  render vision-field draws it like any other entity's cone. */
function computeJanitorFov(janitor: Janitor, map: MapData): void {
  const fov = new Set<number>();
  computeFov(
    janitor.x,
    janitor.y,
    CONFIG.janitor.sightRange,
    (x, y) => blocksVision(tileAt(map, x, y)),
    (x, y) => fov.add(y * map.width + x),
  );
  janitor.fovTiles = fov;
}

/** The janitor spots the intruder: alive, within sight range, lit enough, a
 *  clear line of sight (no wall), and not crouched behind cover. Omnidirectional
 *  — a jumpy janitor notices someone whichever way it's facing. */
function janitorSeesIntruder(janitor: Janitor, player: Player, map: MapData): boolean {
  if (player.body.dead) return false;
  if (player.invisible) return false; // debug ghost
  if (isBlinded(janitor)) return false; // flash-blinded — sees nothing
  const d = Math.max(Math.abs(janitor.x - player.x), Math.abs(janitor.y - player.y));
  if (d > CONFIG.janitor.sightRange) return false;
  if (visibilityFactor(lightLevelAt(map, player.x, player.y)) <= 0) return false; // in the dark
  if (player.stance === 'crouch' && hasCoverBetween(map, janitor.x, janitor.y, player.x, player.y)) return false;
  return losClear(map, janitor.x, janitor.y, player.x, player.y);
}

/** Bresenham line-of-sight: true if no wall/opaque tile lies strictly between. */
function losClear(map: MapData, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  while (true) {
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
    if (x === x1 && y === y1) return true;
    if (!inBounds(map, x, y)) return false;
    if (blocksVision(tileAt(map, x, y))) return false;
  }
}

/** Nearest live guard the janitor can run to and warn. */
function nearestLiveGuard(janitor: Janitor, guards: readonly Guard[]): Guard | null {
  let best: Guard | null = null;
  let bestD = Infinity;
  for (const g of guards) {
    if (g.body.dead) continue;
    const d = (g.x - janitor.x) ** 2 + (g.y - janitor.y) ** 2;
    if (d < bestD) { bestD = d; best = g; }
  }
  return best;
}

/** Deliver the intruder report: the guard goes cautious and investigates the
 *  reported position. */
/** Hand a guard an eyewitness report: they go cautious and search the spot.
 *  Shared with `civilian.ts`, whose panicking workers do the same thing. */
export function informGuard(guard: Guard, px: number, py: number): void {
  const cautious = CONFIG.awareness.thresholds.cautious;
  guard.investigationX = px;
  guard.investigationY = py;
  guard.lastKnownPlayerX = px;
  guard.lastKnownPlayerY = py;
  if (guard.awareness < cautious) guard.awareness = cautious;
  if (guard.state === 'unaware' || guard.state === 'suspicious') guard.state = 'cautious';
  guard.path = null;
}

/** Nearest tile with cleanable blood to (x, y), or null if the floor's clean. */
function nearestBlood(blood: BloodMap, map: MapData, x: number, y: number): { x: number; y: number } | null {
  const thr = CONFIG.janitor.cleanThreshold;
  const w = map.width;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (let idx = 0; idx < blood.blood.length; idx++) {
    if ((blood.blood[idx] ?? 0) < thr) continue;
    const tx = idx % w;
    const ty = (idx / w) | 0;
    if (isBlocked(map, tx, ty)) continue; // unreachable/blocked tile — leave it
    const d = (tx - x) ** 2 + (ty - y) ** 2;
    if (d < bestD) { bestD = d; best = { x: tx, y: ty }; }
  }
  return best;
}
